// ---------------------------------------------------------------------------
// DSS-29: Position controls — position, offsets, z-index, overflow
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Move } from "lucide-react";

// ── Types ──
export type PositionType = "static" | "relative" | "absolute" | "fixed" | "sticky";
export type OverflowType = "visible" | "hidden" | "scroll" | "auto";
export type OffsetUnit = "px" | "%" | "vh" | "vw";

export interface OffsetValue {
  readonly value: number;
  readonly unit: OffsetUnit;
}

export interface PositionConfig {
  readonly position: PositionType;
  readonly top: OffsetValue | null;
  readonly right: OffsetValue | null;
  readonly bottom: OffsetValue | null;
  readonly left: OffsetValue | null;
  readonly zIndex: number;
  readonly overflow: OverflowType;
}

// ── Constants ──
const POSITION_TYPES: readonly PositionType[] = ["static", "relative", "absolute", "fixed", "sticky"];
const OVERFLOW_TYPES: readonly OverflowType[] = ["visible", "hidden", "scroll", "auto"];
const OFFSET_UNITS: readonly OffsetUnit[] = ["px", "%", "vh", "vw"];
const OFFSET_SIDES = ["top", "right", "bottom", "left"] as const;
type OffsetSide = (typeof OFFSET_SIDES)[number];

const MIN_Z_INDEX = 0;
const MAX_Z_INDEX = 9999;
const MIN_OFFSET = -9999;
const MAX_OFFSET = 9999;

const HAS_OFFSETS: readonly PositionType[] = ["absolute", "fixed", "sticky"];

const DEFAULT_CONFIG: PositionConfig = {
  position: "static",
  top: null,
  right: null,
  bottom: null,
  left: null,
  zIndex: 0,
  overflow: "visible",
};

// ── NaN guard ──
function safeNum(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

// ── CSS generator ──
export function generatePositionCss(config: PositionConfig): string {
  const lines: string[] = [];

  if (config.position !== "static") {
    lines.push(`position: ${config.position};`);
  }

  if (HAS_OFFSETS.includes(config.position)) {
    for (const side of OFFSET_SIDES) {
      const offset = config[side];
      if (offset !== null) {
        lines.push(`${side}: ${offset.value}${offset.unit};`);
      }
    }
  }

  if (config.zIndex !== 0) {
    lines.push(`z-index: ${safeNum(config.zIndex, MIN_Z_INDEX, MAX_Z_INDEX)};`);
  }

  if (config.overflow !== "visible") {
    lines.push(`overflow: ${config.overflow};`);
  }

  return lines.length > 0 ? lines.join("\n") : "/* Default positioning */";
}

// ── Props ──
interface PositionControlsProps {
  readonly value: PositionConfig;
  readonly onChange: (config: PositionConfig) => void;
}

// ── Component ──
export default function PositionControls({ value, onChange }: PositionControlsProps) {
  const uid = useId();
  const config = value ?? DEFAULT_CONFIG;
  const showOffsets = HAS_OFFSETS.includes(config.position);

  const handlePositionChange = useCallback(
    (position: PositionType) => {
      onChange({ ...config, position });
    },
    [config, onChange],
  );

  const handleOffsetToggle = useCallback(
    (side: OffsetSide) => {
      const current = config[side];
      if (current !== null) {
        onChange({ ...config, [side]: null });
      } else {
        onChange({ ...config, [side]: { value: 0, unit: "px" } });
      }
    },
    [config, onChange],
  );

  const handleOffsetValue = useCallback(
    (side: OffsetSide, val: number) => {
      const current = config[side];
      if (!current) return;
      onChange({ ...config, [side]: { ...current, value: safeNum(val, MIN_OFFSET, MAX_OFFSET) } });
    },
    [config, onChange],
  );

  const handleOffsetUnit = useCallback(
    (side: OffsetSide, unit: OffsetUnit) => {
      const current = config[side];
      if (!current) return;
      onChange({ ...config, [side]: { ...current, unit } });
    },
    [config, onChange],
  );

  const handleZIndex = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...config, zIndex: safeNum(Number(e.target.value), MIN_Z_INDEX, MAX_Z_INDEX) });
    },
    [config, onChange],
  );

  const handleOverflow = useCallback(
    (overflow: OverflowType) => {
      onChange({ ...config, overflow });
    },
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Move className="h-3.5 w-3.5" />
        Position
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        {/* Position type */}
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Type</span>
          <div className="flex gap-1">
            {POSITION_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handlePositionChange(type)}
                className={[
                  "flex-1 rounded px-1.5 py-1 text-[9px] font-medium capitalize transition-colors",
                  config.position === type
                    ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                    : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
                ].join(" ")}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Offsets — only for absolute/fixed/sticky */}
        {showOffsets && (
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Offsets</span>

            {/* Visual box model */}
            <div className="relative mx-auto flex h-24 w-32 items-center justify-center rounded-md border border-dashed border-[var(--color-border)]">
              {OFFSET_SIDES.map((side) => {
                const offset = config[side];
                const isActive = offset !== null;
                const positionClassMap: Record<OffsetSide, string> = {
                  top: "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2",
                  right: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2",
                  bottom: "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2",
                  left: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2",
                };

                return (
                  <button
                    key={side}
                    type="button"
                    onClick={() => handleOffsetToggle(side)}
                    title={`Toggle ${side} offset`}
                    className={[
                      "absolute flex h-5 w-5 items-center justify-center rounded-full border text-[8px] font-bold transition-colors",
                      positionClassMap[side],
                      isActive
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                        : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]",
                    ].join(" ")}
                  >
                    {side.charAt(0).toUpperCase()}
                  </button>
                );
              })}
              <span className="text-[9px] text-[var(--color-muted-foreground)]">element</span>
            </div>

            {/* Active offset inputs */}
            {OFFSET_SIDES.map((side) => {
              const offset = config[side];
              if (!offset) return null;

              return (
                <OffsetInput
                  key={side}
                  uid={uid}
                  side={side}
                  offset={offset}
                  onValueChange={(v) => handleOffsetValue(side, v)}
                  onUnitChange={(u) => handleOffsetUnit(side, u)}
                />
              );
            })}
          </div>
        )}

        {/* Z-index */}
        <div className="flex items-center gap-2">
          <label
            htmlFor={`${uid}-z`}
            className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
          >
            Z-index
          </label>
          <input
            id={`${uid}-z`}
            type="number"
            min={MIN_Z_INDEX}
            max={MAX_Z_INDEX}
            value={config.zIndex}
            onChange={handleZIndex}
            className="h-6 w-20 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]"
          />
        </div>

        {/* Overflow */}
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Overflow</span>
          <div className="flex gap-1">
            {OVERFLOW_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handleOverflow(type)}
                className={[
                  "flex-1 rounded px-1.5 py-1 text-[9px] font-medium capitalize transition-colors",
                  config.overflow === type
                    ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                    : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
                ].join(" ")}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* CSS preview */}
        <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
          <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
            {generatePositionCss(config)}
          </code>
        </div>
      </div>
    </div>
  );
}

// ── Offset input row ──
interface OffsetInputProps {
  readonly uid: string;
  readonly side: OffsetSide;
  readonly offset: OffsetValue;
  readonly onValueChange: (value: number) => void;
  readonly onUnitChange: (unit: OffsetUnit) => void;
}

function OffsetInput({ uid, side, offset, onValueChange, onUnitChange }: OffsetInputProps) {
  const inputId = `${uid}-offset-${side}`;

  return (
    <div className="flex items-center gap-1.5">
      <label
        htmlFor={inputId}
        className="w-12 shrink-0 text-[10px] font-medium capitalize text-[var(--color-muted-foreground)]"
      >
        {side}
      </label>
      <input
        id={inputId}
        type="number"
        min={MIN_OFFSET}
        max={MAX_OFFSET}
        value={offset.value}
        onChange={(e) => onValueChange(Number(e.target.value))}
        className="h-6 w-16 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]"
      />
      <select
        value={offset.unit}
        onChange={(e) => onUnitChange(e.target.value as OffsetUnit)}
        className="h-6 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1 text-[10px] text-[var(--color-foreground)]"
      >
        {OFFSET_UNITS.map((u) => (
          <option key={u} value={u}>{u}</option>
        ))}
      </select>
    </div>
  );
}
